// The phone's backdrops: what moves behind Home and the Apps screen. Each is
// sized to its container (container query units), so the same component
// fills the screen and draws the little previews in Settings. Colours come
// from the appearance (the sky for the hour, the accent, now's ember), so a
// backdrop is soft in light, quiet in dark and monochrome in black. Motion is
// slow and only transform/opacity, stops under reduced motion, and the one
// canvas (Night) stops drawing while the page is hidden.
import { useEffect, useId, useRef } from 'react'
import { cx } from '@/components/ui'
import type { PhoneWallpaper } from './layout'
import './backdrop.css'

export const BACKDROPS: { id: PhoneWallpaper; name: string; blurb: string }[] = [
  { id: 'dusk', name: 'Dusk', blurb: 'The hour’s sky, a low sun breathing, light along the horizon' },
  { id: 'drift', name: 'Drift', blurb: 'Soft fields of colour moving slowly' },
  { id: 'aurora', name: 'Aurora', blurb: 'Ribbons of light across the top' },
  { id: 'night', name: 'Night', blurb: 'Stars that twinkle, and now and then one falls' },
  { id: 'tide', name: 'Tide', blurb: 'Slow waves along the bottom' },
  { id: 'lines', name: 'Lines', blurb: 'A faint grid with a light passing through. Made for black' },
  { id: 'haunt', name: 'Haunt', blurb: 'Fog drifting low, a few bats and embers. Made for Halloween' },
  { id: 'none', name: 'None', blurb: 'Just the sky behind the app' },
]

export const Backdrop = ({ kind, className }: { kind: PhoneWallpaper; className?: string }) => {
  if (kind === 'none') return null
  return (
    <div className={cx('bd', `bd-${kind}`, className)} aria-hidden data-backdrop={kind}>
      {kind === 'dusk' && (
        <>
          <span className="bd-sky" />
          <span className="bd-sun" />
          <span className="bd-line" />
        </>
      )}
      {kind === 'drift' && (
        <>
          <span />
          <span />
          <span />
          <span />
        </>
      )}
      {kind === 'aurora' && (
        <>
          <span />
          <span />
          <span />
        </>
      )}
      {kind === 'night' && <Stars />}
      {kind === 'tide' && <Tide />}
      {kind === 'lines' && <span className="bd-sweep" />}
      {kind === 'haunt' && (
        <>
          <span className="bd-fog" />
          <span className="bd-fog" />
          <span className="bd-fog" />
          <span className="bd-bat" />
          <span className="bd-bat" />
          <span className="bd-bat" />
          <span className="bd-ember" />
          <span className="bd-ember" />
          <span className="bd-ember" />
          <span className="bd-ember" />
          <span className="bd-ember" />
          <span className="bd-ember" />
        </>
      )}
    </div>
  )
}

/**
 * Three waves, each a strip twice the width sliding by half so the loop has no
 * seam. Each fades from its crest down, so they read as water, not stripes.
 */
const WAVE_TONES = ['var(--bd-1)', 'var(--bd-4)', 'var(--bd-2)']
const Tide = () => {
  const id = useId()
  return (
    <svg className="bd-tide" viewBox="0 0 2400 400" preserveAspectRatio="none">
      <defs>
        {WAVE_TONES.map((tone, i) => (
          <linearGradient key={i} id={`${id}-w${i}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" style={{ stopColor: tone, stopOpacity: 0.34 - i * 0.07 }} />
            <stop offset="1" style={{ stopColor: tone, stopOpacity: 0 }} />
          </linearGradient>
        ))}
      </defs>
      {WAVE_TONES.map((_, i) => (
        <path
          key={i}
          className={`bd-wave bd-wave-${i}`}
          fill={`url(#${id}-w${i})`}
          d="M0,200 C200,110 400,290 600,200 C800,110 1000,290 1200,200 C1400,110 1600,290 1800,200 C2000,110 2200,290 2400,200 L2400,400 L0,400 Z"
          transform={`translate(0 ${i * 52})`}
        />
      ))}
    </svg>
  )
}

/**
 * The starfield: a few hundred points that twinkle and drift up very slowly,
 * with a falling star every so often. 30 frames a second is plenty for this,
 * and nothing is drawn while the page is hidden.
 */
const Stars = () => {
  const ref = useRef<HTMLCanvasElement>(null)
  useEffect(() => {
    const c = ref.current
    const ctx = c?.getContext('2d')
    if (!c || !ctx) return
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const dpr = Math.min(2, window.devicePixelRatio || 1)
    let w = 0
    let h = 0
    let stars: { x: number; y: number; r: number; v: number; a: number; s: number }[] = []
    let fall: { x: number; y: number; vx: number; vy: number; life: number } | null = null
    let nextFall = performance.now() + 6000
    const colour = () => getComputedStyle(c).getPropertyValue('--bd-star').trim() || '#ffffff'
    let ink = colour()
    const size = () => {
      w = c.clientWidth
      h = c.clientHeight
      c.width = Math.round(w * dpr)
      c.height = Math.round(h * dpr)
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      const n = Math.round((w * h) / 2600)
      stars = Array.from({ length: n }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        r: Math.random() * 1.1 + 0.25,
        v: Math.random() * 0.012 + 0.003,
        a: Math.random() * Math.PI * 2,
        s: Math.random() * 0.8 + 0.4,
      }))
      ink = colour()
    }
    size()
    const ro = new ResizeObserver(size)
    ro.observe(c)
    let raf = 0
    let last = performance.now()
    const frame = (now: number) => {
      raf = requestAnimationFrame(frame)
      const dt = now - last
      if (dt < 33) return
      last = now
      ctx.clearRect(0, 0, w, h)
      ctx.fillStyle = ink
      for (const s of stars) {
        if (!still) {
          s.y -= s.v * dt * 0.06
          if (s.y < -2) {
            s.y = h + 2
            s.x = Math.random() * w
          }
          s.a += 0.0012 * dt * s.s
        }
        ctx.globalAlpha = 0.25 + 0.55 * (0.5 + 0.5 * Math.sin(s.a))
        ctx.beginPath()
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2)
        ctx.fill()
      }
      if (!still && !fall && now > nextFall) {
        fall = {
          x: Math.random() * w * 0.7 + w * 0.15,
          y: Math.random() * h * 0.3,
          vx: -0.35,
          vy: 0.18,
          life: 1,
        }
        nextFall = now + 9000 + Math.random() * 12000
      }
      if (fall) {
        const tail = 60
        const g = ctx.createLinearGradient(fall.x, fall.y, fall.x - fall.vx * tail, fall.y - fall.vy * tail)
        g.addColorStop(0, ink)
        g.addColorStop(1, 'transparent')
        ctx.globalAlpha = fall.life
        ctx.strokeStyle = g
        ctx.lineWidth = 1.4
        ctx.beginPath()
        ctx.moveTo(fall.x, fall.y)
        ctx.lineTo(fall.x - fall.vx * tail, fall.y - fall.vy * tail)
        ctx.stroke()
        fall.x += fall.vx * dt
        fall.y += fall.vy * dt
        fall.life -= dt / 1400
        if (fall.life <= 0) fall = null
      }
      ctx.globalAlpha = 1
    }
    const start = () => {
      cancelAnimationFrame(raf)
      last = performance.now()
      raf = requestAnimationFrame(frame)
    }
    const onVis = () => (document.hidden ? cancelAnimationFrame(raf) : start())
    document.addEventListener('visibilitychange', onVis)
    // the appearance can change under it (auto turns at 07:00 and 19:00)
    const mo = new MutationObserver(() => (ink = colour()))
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-appearance', 'style'] })
    start()
    return () => {
      cancelAnimationFrame(raf)
      ro.disconnect()
      mo.disconnect()
      document.removeEventListener('visibilitychange', onVis)
    }
  }, [])
  return (
    <>
      <span className="bd-glow" />
      <canvas ref={ref} className="bd-canvas" />
    </>
  )
}
